import React, { useEffect, useState } from "react";
import { api, formatINR } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["confirmed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/admin/orders", { params: statusFilter ? { status: statusFilter } : {}, withCredentials: true }).then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (order_number, status) => {
    try {
      await api.put(`/admin/orders/${order_number}`, { status, note: `Status updated to ${status}` }, { withCredentials: true });
      toast.success("Order updated");
      load();
      if (selected?.order_number === order_number) setSelected(null);
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-orders">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="label-tiny text-brand-gold">Fulfillment</p>
          <h1 className="font-serif-brand text-4xl text-brand-charcoal mt-1">Orders · {orders.length}</h1>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-brand-gold/40 px-3 py-2 text-sm bg-brand-ivory outline-none" data-testid="status-filter">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div className="border border-brand-gold/25 bg-brand-ivory overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-ivory-soft">
            <tr className="text-left label-tiny text-brand-muted">
              <th className="p-4">Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Placed</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_number} className="border-t border-brand-gold/15" data-testid={`order-row-${o.order_number}`}>
                <td className="p-4 font-medium text-brand-charcoal">{o.order_number}</td>
                <td className="p-4"><p>{o.shipping.full_name}</p><p className="text-xs text-brand-muted">{o.shipping.phone}</p></td>
                <td className="p-4">{o.items.length}</td>
                <td className="p-4 text-brand-maroon font-medium">{formatINR(o.total)}</td>
                <td className="p-4"><span className="label-tiny bg-brand-gold/20 text-brand-maroon px-2 py-1 uppercase">{o.status}</span></td>
                <td className="p-4 text-xs text-brand-muted">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-4 text-right"><button onClick={() => setSelected(o)} className="text-brand-maroon hover:underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/60 flex items-start justify-center p-4 lg:p-10 overflow-y-auto" onClick={() => setSelected(null)} data-testid="order-detail-modal">
          <div className="bg-brand-ivory w-full max-w-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="label-tiny text-brand-gold">{selected.status.toUpperCase()}</p>
                <h2 className="font-serif-brand text-3xl text-brand-charcoal mt-1">Order {selected.order_number}</h2>
              </div>
              <p className="text-brand-maroon font-serif-brand text-2xl">{formatINR(selected.total)}</p>
            </div>

            <p className="label-tiny text-brand-muted mb-2">Update Status</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => updateStatus(selected.order_number, s)} className={`text-xs uppercase tracking-[0.15em] px-3 py-2 border ${selected.status === s ? "bg-brand-maroon text-brand-ivory border-brand-maroon" : "border-brand-gold/40 text-brand-charcoal hover:border-brand-maroon"}`} data-testid={`set-status-${s}`}>{s.replace(/_/g, ' ')}</button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="label-tiny text-brand-gold mb-2">Ship To</p>
                <p className="text-brand-charcoal">{selected.shipping.full_name}</p>
                <p className="text-brand-muted">{selected.shipping.line1}{selected.shipping.line2 ? `, ${selected.shipping.line2}` : ""}</p>
                <p className="text-brand-muted">{selected.shipping.city}, {selected.shipping.state} - {selected.shipping.pincode}</p>
                <p className="text-brand-muted mt-1">📞 {selected.shipping.phone} · {selected.shipping.email}</p>
              </div>
              <div>
                <p className="label-tiny text-brand-gold mb-2">Payment</p>
                <p>{selected.payment_method}</p>
                <p className="text-xs text-brand-muted mt-1">Subtotal: {formatINR(selected.subtotal)}</p>
                <p className="text-xs text-brand-muted">Shipping: {formatINR(selected.shipping_fee)}</p>
                {selected.discount > 0 && <p className="text-xs text-brand-muted">Discount: -{formatINR(selected.discount)}</p>}
              </div>
            </div>

            <p className="label-tiny text-brand-gold mt-6 mb-3">Items</p>
            <div className="space-y-2">
              {selected.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <img src={it.image} alt="" className="w-12 h-12 object-cover" />
                  <div className="flex-1">{it.name} <span className="text-brand-muted">× {it.quantity}</span></div>
                  <span className="text-brand-charcoal">{formatINR(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>

            <p className="label-tiny text-brand-gold mt-6 mb-3">Tracking Timeline</p>
            <div className="space-y-2 text-sm">
              {selected.tracking_updates.map((u, i) => (
                <div key={i} className="border-l-2 border-brand-gold pl-3">
                  <p className="text-brand-charcoal font-medium">{u.status}</p>
                  <p className="text-xs text-brand-muted">{u.note} · {new Date(u.at).toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
